// ===================================================
// uiText.js — 서비스 내 모든 UI 텍스트 일원 관리 파일
//
// 이 파일 하나에서 앱 전체의 버튼 레이블, 안내 문구,
// 플레이스홀더 등 모든 텍스트를 수정할 수 있습니다.
//
// 사용법: import { UI_TEXT } from './content/uiText';
//         const T = UI_TEXT.qa;  → T.endBtn 등으로 사용
// ===================================================

export const UI_TEXT = {

  // ── 모바일 하단 네비게이션 (FNB) ───────────────────────────
  // 아이콘은 MobileNav.js에서 SVG로 직접 렌더링합니다.
  mobileNav: {
    create:   '이야기하기',
    search:   '검색',
    calendar: '내 기록',
    chat:     '나와의 대화',
    settings: '설정',
  },

  // ── 좌측 사이드바 (데스크톱) ────────────────────────────────
  sidebar: {
    title:             '내 이야기',
    homeBtn:           '홈',
    newRecord:         '이야기하기',
    chatBtn:           '나와의 대화',
    calendarBtn:       '내 기록',
    allKeywords:       '전체 키워드',
    empty:             '아직 이야기가 없어요',
    modeKeywords:      (label) => `${label} 키워드`,
    searchPlaceholder: '이야기 검색...',
    keywordsSection:   '키워드',
    moreRecords:       (n) => `${n}개 더보기`,
    collapse:          '접기',
    settings:          '설정',
    pinnedSection:     '즐겨찾기',
    noPinned:          '즐겨찾기한 기록이 없어요',
  },

  // ── 기록 페이지 (탭 구조: 캘린더 + 즐겨찾기 + 리포트) ──────────
  records: {
    homeBtn:         '← 홈으로',
    title:           '기록',
    calendarTab:     '캘린더',
    pinnedTab:       '즐겨찾기',
    reportTab:       '리포트',
    pinnedEmpty:     '아직 즐겨찾기한 기록이 없어요',
    pinnedEmptyHint: '기록 상세 화면에서 ☆ 버튼으로 추가해보세요',
  },

  // ── 기록 캘린더 화면 ─────────────────────────────────────────
  calendar: {
    title:          '캘린더',
    homeBtn:        '← 홈으로',
    streakLabel:    '연속 이야기 🔥',
    totalLabel:     '총 이야기',
    monthLabel:     '이번 달',
    legendRecorded: '이야기한 날',
    tapHint:        '이야기한 날을 탭하면 내용을 볼 수 있어요',
    emptyHint:      '아직 이야기가 없어요. 첫 이야기를 시작해볼까요?',
  },

  // ── 나와의 대화 채팅 화면 ────────────────────────────────────
  chat: {
    title:        '나와의 대화',
    homeBtn:      '← 홈으로',
    placeholder:  '무슨 이야기 할까요?',
    sendBtn:      '보내기',
    welcome:      '지금까지 나눈 이야기를 기억하고 있어요.',
    noMemory:     '이야기를 나눌수록 나를 더 잘 알게 돼요.',
    loading:      '생각하는 중...',
    error:        '오류가 발생했어요. 다시 시도해주세요.',
    starterLabel: '이런 것들을 물어볼 수 있어요',
    starters: [
      '요즘 내 이야기에서 어떤 패턴이 보여?',
      '내가 자주 고민하는 게 뭔지 알아?',
      '최근에 내가 가장 많이 한 이야기는?',
    ],
    startersNoMemory: [
      '오늘 하루 어떠냐고 물어봐줘',
      '요즘 내 고민을 들어줘',
      '나 좀 응원해줘',
    ],
  },

  // ── 랜딩(홈) 화면 ──────────────────────────────────────────
  landing: {
    nextBtn:        '다음으로 →',
    analyzing:      '분석 중...',
    freewriteLabel: 'AI 문답 없이 자유 기록',
    saveBtn:        '저장하기',
    saving:         '저장 중...',
  },

  // ── QA(질문-답변) 화면 ─────────────────────────────────────
  qa: {
    title:             '이야기 나눠볼까요',
    regenHint:         '질문이 마음에 들지 않으면, 새로고침 버튼을 눌러주세요',
    answerPlaceholder: '여기에 적어주세요...',
    continueBtn:       '계속 이야기하기 →',
    endBtn:            '저장하고 마치기',
    saving:            '저장 중...',
    regenTooltip:      '다른 질문으로',
    reportTooltip:     '이 질문 신고',
    reportTitle:       '이 질문이 마음에 들지 않는 이유를 알려주세요',
    reportPlaceholder: '예: 너무 뻔한 질문이에요 / 맥락과 안 맞아요',
    reportSubmit:      '제출',
    reportCancel:      '취소',
  },

  // ── 세션 상세 보기 화면 ────────────────────────────────────
  session: {
    homeBtn:           '← 홈으로',
    deleteBtn:         '삭제',
    editBtn:           '수정',
    saveBtn:           '저장',
    cancelBtn:         '취소',
    reSaving:          '저장 중...',
    deleteConfirm:     '이 이야기를 삭제할까요? 삭제하면 되돌릴 수 없어요.',
    summaryLabel:      '하루 요약',
    freewriteLabel:    '자유 기록',
    keywordsLabel:     '핵심 키워드',
    roundLabel:        (n) => `Round ${n}`,
    createdAt:         '생성',
    updatedAt:         '수정',
    answerPlaceholder: '여기에 적어주세요...',
    pinBtn:            '★',   // 즐겨찾기 켜기 버튼 아이콘
    unpinBtn:          '☆',   // 즐겨찾기 끄기 버튼 아이콘
  },

  // ── 키워드 목록 페이지 ─────────────────────────────────────
  keywords: {
    homeBtn:   '← 홈으로',
    allTitle:  '전체 키워드',
    modeTitle: (label) => `${label} 키워드`,
    empty:     '아직 키워드가 없어요.\n기록을 저장하면 AI가 키워드를 자동으로 추출해드려요.',
    count:     (n) => `총 ${n}개의 키워드`,
  },

  // ── 키워드 검색 결과 화면 ─────────────────────────────────
  keywordSearch: {
    backBtn:     '← 키워드로',
    recordCount: (n) => `${n}개의 이야기`,
    empty:       '이 키워드가 포함된 이야기가 없어요.',
  },

  // ── 모바일 기록 목록 페이지 ───────────────────────────────
  mobileRecords: {
    title:        '내 이야기',
    closeBtn:     '✕',
    allKeywords:  '🏷️ 전체 키워드',
    modeKeywords: '🏷️ 키워드',
    empty:        '아직 이야기가 없어요',
  },

  // ── 검색 페이지 ────────────────────────────────────────────
  search: {
    title:              '검색',
    placeholder:        '검색어를 입력해주세요',
    searchBtn:          '검색',
    homeBtn:            '← 홈으로',
    section1:           '유사 키워드',
    section2:           '관련 이야기',
    noKeywords:         '유사한 키워드가 없어요',
    noRecords:          '관련 이야기가 없어요',
    loadMore:           '더보기',
    loading:            '검색 중...',
    noExistingKeywords: '아직 키워드가 없어요. 먼저 이야기를 시작해보세요.',
    recordCount:        (n) => `${n}개의 이야기`,
  },

  // ── 설정 페이지 ────────────────────────────────────────────
  settings: {
    title:            '설정',
    homeBtn:          '← 홈으로',
    themeSection:     '화면 테마',
    lightMode:        '라이트 모드',
    darkMode:         '다크 모드',
    timezoneSection:  '시간대',
    timezoneHint:     '설정하지 않으면 현재 브라우저 시간대가 적용돼요.',
    modelSection:     'GPT 모델',
    versionSection:   '버전',
    deleteSection:    '데이터',
    deleteAllBtn:          '모든 이야기 삭제',
    deleteAllConfirm:      '모든 이야기를 삭제할까요? 삭제하면 되돌릴 수 없어요.',
    deleteAccountSection:  '계정 삭제',
    deleteAccountBtn:      '계정 삭제',
    deleteAccountConfirm:  '계정을 삭제하면 모든 기록, 채팅 내역, 계정 정보가 영구적으로 삭제돼요. 삭제 후에는 되돌릴 수 없어요. 정말 삭제할까요?',
  },

};
